/**
 * 에셋 해석기.
 *
 * 1) public/assets/manifest.json 을 읽는다 (없으면 기본값).
 * 2) 각 슬롯의 파일이 실제로 있는지 확인한다 (autoDetect 이면 HEAD 요청).
 * 3) 있으면 스프라이트시트로 로드, 없으면 코드 생성 도트 아트를 굽는다.
 * 4) 해석 결과(AssetTable)를 게임 전역에 제공한다.
 */

import type Phaser from 'phaser';
import { bakeSheet } from './bake';
import { ASSET_SLOTS, type AssetSlot } from './registry';

const ASSET_BASE = 'assets';

interface ManifestOverride {
  file?: string;
  frameWidth?: number;
  frameHeight?: number;
  frames?: number;
  displayHeight?: number;
  disabled?: boolean;
}

interface Manifest {
  autoDetect?: boolean;
  overrides?: Record<string, ManifestOverride>;
}

export interface ResolvedAsset {
  key: string;
  frames: number;
  frameWidth: number;
  frameHeight: number;
  displayHeight: number;
  /** 'generated' = 코드 생성 도트 아트, 'file' = 사용자 제공 파일 */
  source: 'generated' | 'file';
  file?: string;
  group: string;
}

export class AssetTable {
  private readonly map = new Map<string, ResolvedAsset>();

  set(entry: ResolvedAsset): void {
    this.map.set(entry.key, entry);
  }

  get(key: string): ResolvedAsset {
    const found = this.map.get(key);
    if (!found) throw new Error(`등록되지 않은 에셋 키: ${key}`);
    return found;
  }

  /** 오버라이드 스프라이트 크기가 달라도 게임 내 크기를 유지하기 위한 배율 */
  scaleFor(key: string): number {
    const a = this.get(key);
    return a.displayHeight / a.frameHeight;
  }

  frameCount(key: string): number {
    return this.get(key).frames;
  }

  all(): ResolvedAsset[] {
    return [...this.map.values()];
  }

  summary(): { file: number; generated: number } {
    let file = 0;
    let generated = 0;
    for (const a of this.map.values()) {
      if (a.source === 'file') file++;
      else generated++;
    }
    return { file, generated };
  }
}

async function loadManifest(): Promise<Manifest> {
  try {
    const res = await fetch(`${ASSET_BASE}/manifest.json`, { cache: 'no-store' });
    if (!res.ok) return {};
    const json = (await res.json()) as Manifest;
    return json ?? {};
  } catch {
    return {};
  }
}

/** 파일이 실제로 존재하는지 확인. dev 서버가 index.html 을 폴백으로 돌려주는 경우도 걸러낸다. */
async function fileExists(url: string): Promise<boolean> {
  try {
    const res = await fetch(url, { method: 'HEAD', cache: 'no-store' });
    if (!res.ok) return false;
    const type = res.headers.get('content-type') ?? '';
    return !type.includes('text/html');
  } catch {
    return false;
  }
}

interface PendingOverride {
  slot: AssetSlot;
  url: string;
  frames: number;
  frameWidth: number;
  frameHeight: number;
  displayHeight: number;
}

/**
 * 오버라이드 대상을 탐지한다. Phaser 로더에 넣기 전에 끝나야 하므로 비동기로 먼저 수행한다.
 */
export async function detectOverrides(): Promise<{
  manifest: Manifest;
  pending: PendingOverride[];
}> {
  const manifest = await loadManifest();
  const overrides = manifest.overrides ?? {};
  const autoDetect = manifest.autoDetect !== false;

  interface Candidate {
    slot: AssetSlot;
    ov: ManifestOverride | undefined;
    file: string;
  }

  const candidates: Candidate[] = [];
  for (const slot of ASSET_SLOTS) {
    const ov = overrides[slot.key];
    if (ov?.disabled) continue;
    if (ov === undefined && !autoDetect) continue;
    candidates.push({ slot, ov, file: ov?.file ?? slot.file });
  }

  const results = await Promise.all(
    candidates.map(async (c) => {
      const url = `${ASSET_BASE}/${c.file}`;
      const exists = await fileExists(url);
      if (!exists) return null;
      const defaults = defaultFrameSize(c.slot);
      const frameWidth = c.ov?.frameWidth ?? defaults.frameWidth;
      const frameHeight = c.ov?.frameHeight ?? defaults.frameHeight;
      return {
        slot: c.slot,
        url,
        frames: c.ov?.frames ?? c.slot.frames,
        frameWidth,
        frameHeight,
        displayHeight: c.ov?.displayHeight ?? c.slot.displayHeight ?? frameHeight,
      } satisfies PendingOverride;
    }),
  );

  return { manifest, pending: results.filter((r): r is PendingOverride => r !== null) };
}

function defaultFrameSize(slot: AssetSlot): { frameWidth: number; frameHeight: number } {
  const grid = slot.grids()[0];
  return {
    frameWidth: (grid[0]?.length ?? 0) * slot.scale,
    frameHeight: grid.length * slot.scale,
  };
}

/** 탐지된 오버라이드를 Phaser 로더에 등록한다. */
export function queueOverrides(scene: Phaser.Scene, pending: readonly PendingOverride[]): void {
  for (const p of pending) {
    scene.load.spritesheet(p.slot.key, p.url, {
      frameWidth: p.frameWidth,
      frameHeight: p.frameHeight,
    });
  }
}

/**
 * 오버라이드로 로드되지 않은 슬롯은 코드 생성 아트로 굽고, 최종 해석 결과를 만든다.
 * (로더 완료 후에 호출해야 한다)
 */
export function resolveAssets(
  scene: Phaser.Scene,
  pending: readonly PendingOverride[],
): AssetTable {
  const table = new AssetTable();
  const overridden = new Map(pending.map((p) => [p.slot.key, p]));

  for (const slot of ASSET_SLOTS) {
    const ov = overridden.get(slot.key);
    if (ov && scene.textures.exists(slot.key)) {
      const tex = scene.textures.get(slot.key);
      const actualFrames = tex.frameTotal - 1; // __BASE 제외
      table.set({
        key: slot.key,
        frames: Math.max(1, Math.min(ov.frames, actualFrames)),
        frameWidth: ov.frameWidth,
        frameHeight: ov.frameHeight,
        displayHeight: ov.displayHeight,
        source: 'file',
        file: ov.url,
        group: slot.group,
      });
      continue;
    }

    const grids = slot.grids();
    const baked = bakeSheet(scene, slot.key, grids, slot.palette, slot.scale);
    table.set({
      key: slot.key,
      frames: grids.length,
      frameWidth: baked.frameWidth,
      frameHeight: baked.frameHeight,
      displayHeight: slot.displayHeight ?? baked.frameHeight,
      source: 'generated',
      group: slot.group,
    });
  }

  return table;
}
